import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Users } from 'lucide-react';
import EnrollmentModal from './enrollment/EnrollmentModal';
import { allCourses, courseDetails } from '../data/courses';
import { getFeaturedCourses } from '../lib/lms';
import type { LmsCourse } from '../types/lms';

interface FeaturedCard {
  id: string;
  title: string;
  category: string;
  description: string;
  duration: string;
  students: string;
  level: string;
  price: string;
  priceValue: number;
  image: string;
}

const fallbackImage = allCourses[0]?.image || '';

const fallbackCards: FeaturedCard[] = [allCourses[0], allCourses[3], allCourses[5]]
  .filter(Boolean)
  .map((course) => ({
    id: course.id,
    title: course.title,
    category: course.category,
    description: course.description,
    duration: course.duration,
    students: course.students,
    level: course.level,
    price: course.price,
    priceValue: courseDetails[course.id]?.priceValue ?? 0,
    image: course.image,
  }));

const toCard = (course: LmsCourse): FeaturedCard => ({
  id: course.id,
  title: course.title,
  category: course.category || 'Programme',
  description: course.tagline || course.description || '',
  duration: course.duration || 'Self-paced',
  students: 'New cohort',
  level: course.level || 'All levels',
  price: course.price_value === 0 ? 'Free' : `₦${course.price_value.toLocaleString('en-NG')}`,
  priceValue: course.price_value,
  image: course.thumbnail || fallbackImage,
});

const FeaturedCourses = () => {
  const [featured, setFeatured] = useState<FeaturedCard[]>(fallbackCards);
  const [selectedCourse, setSelectedCourse] = useState<FeaturedCard | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void getFeaturedCourses(3).then((courses) => {
      if (cancelled) return;
      const cards = courses
        .filter((course) => course.category !== 'WhatsApp')
        .map(toCard);
      if (cards.length > 0) setFeatured(cards);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const handleEnrollClick = (course: FeaturedCard) => {
    setSelectedCourse(course);
    setIsModalOpen(true);
  };

  return (
    <section id="featured-courses" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-14">
          <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-sm font-medium mb-5">
            Featured Programs
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Programs that turn ambition into export revenue
          </h2>
          <p className="mt-5 text-lg text-gray-600">
            From an 8-week starter certificate to a 12-month Mini MBA — pick the depth that
            matches where you're headed.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {featured.map((course) => (
            <div
              key={course.id}
              className="group flex flex-col bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-blue-300 hover:shadow-xl transition-all"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={course.image}
                  alt={course.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/95 text-xs font-semibold text-blue-700">
                    {course.category}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-white font-semibold text-sm">
                  {course.level}
                </div>
              </div>

              <div className="flex flex-col flex-1 p-6">
                <Link
                  to={`/course/${course.id}`}
                  className="block text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors mb-3"
                >
                  {course.title}
                </Link>
                <p className="text-gray-600 text-sm leading-relaxed flex-1">
                  {course.description}
                </p>

                <div className="mt-5 grid grid-cols-2 gap-3 text-sm text-gray-500">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-blue-500" />
                    {course.duration}
                  </div>
                  <div className="flex items-center">
                    <Users className="h-4 w-4 mr-2 text-blue-500" />
                    {course.students}
                  </div>
                </div>

                <div className="mt-6 pt-5 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xl font-bold text-gray-900">{course.price}</span>
                  <button
                    onClick={() => handleEnrollClick(course)}
                    className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 transition-colors"
                  >
                    Enroll
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/courses"
            className="inline-flex items-center px-6 py-3 rounded-lg border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-semibold transition-colors"
          >
            View All Programs
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>

      {selectedCourse && (
        <EnrollmentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          courseId={selectedCourse.id}
          courseTitle={selectedCourse.title}
          coursePrice={selectedCourse.price}
          priceValue={selectedCourse.priceValue}
        />
      )}
    </section>
  );
};

export default FeaturedCourses;
